import { createSelector } from 'reselect';
import first from 'lodash/first';
import get from 'lodash/get';
import last from 'lodash/last';
import values from 'lodash/values';
import { type OperatorValues, type QuestionType, operatorMap } from '../reducers';
import type { UserAnswersT } from '../reducers/answers';
const getSelectedAnswers = (question: QuestionType, userAnswers: UserAnswersT) => {
  const answers = get(question, 'answers');
  if (!answers) return {};
  const selectedAnswers = answers.filter(answer => userAnswers.includes(answer.answer));
  return selectedAnswers;
};
const delimiter = '==';
const getKeyValue = answer => [first(answer.split(delimiter)), last(answer.split(delimiter))];
const getValueFromPath = (answer, metaKey) => {
  return get(answer, ['metadata', ...metaKey.split('.')]);
};
export const getSelectedAnswer = (userAnswers: UserAnswersT, answer: string, operator: OperatorValues, question: QuestionType): boolean => {
  if (!answer) {
    return false;
  }
  switch (operator) {
    case operatorMap.metadata_not_equals:
    case operatorMap.metadata_includes:
    case operatorMap.metadata_not_includes:
    case operatorMap.metadata_equals:
      {
        if (answer && !answer.includes(delimiter)) {
          console.warn('condition value does not contain delimiter', delimiter);
        }
      }
  }
  switch (operator) {
    case operatorMap.equals:
      return userAnswers.includes(answer);
    case operatorMap.not_equals:
      return !userAnswers.some(userAnswer => userAnswer === answer);
    case operatorMap.includes:
      return userAnswers.some(userAnswer => !(userAnswer instanceof File) && userAnswer.includes(answer));
    case operatorMap.not_includes:
      return !userAnswers.some(userAnswer => !(userAnswer instanceof File) && userAnswer.includes(answer));
    case operatorMap.metadata_equals:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);

        // @ts-expect-error todo(ts-migration) TS2339 Property 'some' does not exist on type '{}'.
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey);
          return answerMetaValue === metaValue;
        });
        return matched;
      }
    case operatorMap.metadata_not_equals:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);

        // @ts-expect-error todo(ts-migration) TS2339 Property 'some' does not exist on type '{}'.
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey);
          return answerMetaValue === metaValue;
        });
        return !matched;
      }
    case operatorMap.metadata_includes:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);

        // @ts-expect-error todo(ts-migration) TS2339 Property 'some' does not exist on type '{}'.
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey) || '';
          // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'unknown' is not assignable to parameter of type 'string'.
          return answerMetaValue.includes(metaValue);
        });
        return matched;
      }
    case operatorMap.metadata_not_includes:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);

        // @ts-expect-error todo(ts-migration) TS2339 Property 'some' does not exist on type '{}'.
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey) || '';
          // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'unknown' is not assignable to parameter of type 'string'.
          return answerMetaValue.includes(metaValue);
        });
        return !matched;
      }
  }
  return false;
};
export const isSelected = createSelector((userAnswers: UserAnswersT): UserAnswersT => userAnswers || [], (_, answer): string => answer, (_, __, operator): string => operator || 'equals', (_, __, ___, question): QuestionType => question, getSelectedAnswer);
export const defaultAnswer = {
  answer: '',
  note: 'none'
};
export const noteOptions = {
  none: 'none',
  optional: 'optional',
  required: 'required'
};
export const noteOptionValues = values(noteOptions);
export const prepareAnswer = (answerCopy: any, noteOption: string | undefined | null = defaultAnswer.note, defaultShape: any = defaultAnswer) => {
  let note = noteOption;
  // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | null' is not assignable to parameter of type 'string'.
  if (!noteOptionValues.includes(note)) {
    note = defaultAnswer.note;
  }
  return {
    ...defaultShape,
    answer: answerCopy,
    note
  };
};
export const checkFileType = (file: File, type: string) => file.type.includes(type) || file.name.endsWith(`.${type}`);
export function isEmpty(value: any): boolean {
  return typeof value === 'undefined' || value === null || (typeof value === 'string' || value instanceof Array === true) && value.length < 1 || typeof value === 'object' && Object.keys(value).length < 1 && value.constructor === Object && !(value instanceof Error) || typeof value === 'number' && Number.isNaN(value);
}
export function isNil(value: any): boolean {
  return value == null;
}
export function extractSizeAttr(sizeStr: string) {
  const [size, unit] = sizeStr.split(' ');
  return {
    size: Number(size) || 0,
    unit: unit || 'Bytes'
  };
}
export function getSizeInBytes(sizeStr: string): number {
  const {
    size,
    unit
  } = extractSizeAttr(sizeStr);
  switch (unit) {
    case 'MB':
      return Number(size) * 1000 * 1000;
    case 'KB':
      return Number(size) * 1000;
    default:
      return Number(size);
  }
}
export function toFixedString(numStr: string | number, maxDecimals = 2) {
  const [integer, decimal] = String(numStr).split('.');
  return `${!isNaN(Number(integer)) ? Number(integer) : ''}${!isNaN(Number(decimal)) ? `.${decimal.slice(0, maxDecimals)}` : ''}`;
}
export function getSizeInUnit(size: number, unit: string, maxDecimals = 2): string {
  let newSize;
  switch (unit) {
    case 'MB':
      newSize = size / (1000 * 1000);
      break;
    case 'KB':
      newSize = size / 1000;
      break;
    default:
      newSize = size / 1000;
  }
  return `${toFixedString(newSize, maxDecimals)} ${unit}`;
}